import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { userAPI, groupAPI, stackAPI, botAPI } from '../services/api';
import apiCache from '../services/cache';
import Stacks from './Stacks';
import StackProfile from './StackProfile';
import TwistedStackProfile from './TwistedStackProfile';
import { getDiceBearUserAvatar } from '../services/dicebear';
import { getStackDisplayName } from '../utils/stack';
import { confirmAction } from '../utils/confirmationDialog';
import { BUTTON_COLORS } from '../utils/buttonColors';
import { createSortaFriendsClickHandler } from '../utils/navigation';
import CreateStack from './CreateStack';
// import { formatRoleDisplay } from '../utils/role'; // ignore roles while beta testing
import Notifications from './Notifications';
import Groups from './Groups';
import GroupProfile from './GroupProfile';
import BotProfile from './BotProfile';
import SocialSettings from './SocialSettings';
import EditableTextArea from './EditableTextArea';
import Tour from './Tour';


const Dashboard = () => {
  const [activeTab, setActiveTab] = useState('stacks');
  const [users, setUsers] = useState([]);
  const [bots, setBots] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [error, setError] = useState('');
  const [avatarMessage, setAvatarMessage] = useState('');
  const [bioMessage, setBioMessage] = useState('');
  const [bioText, setBioText] = useState('');
  const [themeSetting, setThemeSetting] = useState('dark'); // 'system' | 'light' | 'dark'
  const [friendRequestSent, setFriendRequestSent] = useState(false);
  const [showUnfriendConfirm, setShowUnfriendConfirm] = useState(false);
  const [showBlockConfirm, setShowBlockConfirm] = useState(false);
  const [showDeactivateConfirm, setShowDeactivateConfirm] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const [showGroups, setShowGroups] = useState(false);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [selectedBot, setSelectedBot] = useState(null);
  const [selectedStack, setSelectedStack] = useState(null);
  const [selectedTwistedStack, setSelectedTwistedStack] = useState(null);
  const [selectedStackName, setSelectedStackName] = useState(null);
  const [selectedGroupName, setSelectedGroupName] = useState(null);
  const [showCreateGroupDialog, setShowCreateGroupDialog] = useState(false);
  const [createGroupForm, setCreateGroupForm] = useState({
    name: '',
    description: '',
    is_public: true,
    is_restricted: false,
    is_readonly: false
  });
  const [createStackDialog, setCreateStackDialog] = useState({ isOpen: false, mode: null, targetId: null, targetName: null, targetAvatar: null });
  
  // User data state
  const [currentUser, setCurrentUser] = useState(null);
  const [currentUserLoading, setCurrentUserLoading] = useState(true);
  
  // Shared activity state
  const [sharedActivity, setSharedActivity] = useState(null);
  const [sharedActivityLoading, setSharedActivityLoading] = useState(false);
  
  // User profile loading state
  const [userProfileLoading, setUserProfileLoading] = useState(false);
  const [currentUserError, setCurrentUserError] = useState(null);
  const [unreadNotificationsCount, setUnreadNotificationsCount] = useState(0);
  const [updateBioLoading, setUpdateBioLoading] = useState(false);
  const [hasAutoShownNotifications, setHasAutoShownNotifications] = useState(false);
  const [currentNotificationIds, setCurrentNotificationIds] = useState([]);
  const [notificationsData, setNotificationsData] = useState([]);
  
  // Refs to track loading states without causing dependency issues
  const currentUserLoadingRef = useRef(false);
  const notificationsLoadingRef = useRef(false);
  
  // Server error overlay state
  const [showServerError, setShowServerError] = useState(false);
  const [serverErrorMessage, setServerErrorMessage] = useState('');
  
  // Load current user data
  const loadCurrentUser = useCallback(async () => {
    // Prevent concurrent calls
    if (currentUserLoadingRef.current) {
      return;
    }
    
    try {
      currentUserLoadingRef.current = true;
      setCurrentUserLoading(true);
      setCurrentUserError(null);
      const response = await userAPI.getCurrentUser();
      setCurrentUser(response.data);
    } catch (error) {
      setCurrentUserError(error);
      console.error('Failed to load current user:', error);
    } finally {
      currentUserLoadingRef.current = false;
      setCurrentUserLoading(false);
    }
  }, []);
  
  // Load unread notifications count and IDs
  const loadUnreadNotificationsCount = useCallback(async () => {
    // Prevent concurrent calls
    if (notificationsLoadingRef.current) {
      return;
    }
    
    try {
      notificationsLoadingRef.current = true;
      const [countResponse, notificationsResponse] = await Promise.all([
        userAPI.getUnreadNotificationsCount(),
        userAPI.getNotifications()
      ]);
      
      const count = countResponse.data.unread_count || 0;
      const notifications = notificationsResponse.data || [];
      
      // Get IDs of notifications (backend already filters to unread + pending)
      const unreadNotificationIds = notifications.map(notif => notif.id);
      
      setUnreadNotificationsCount(count);
      setCurrentNotificationIds(unreadNotificationIds);
      setNotificationsData(notifications);
    } catch (error) {
      console.error('Failed to load unread notifications count:', error);
    } finally {
      notificationsLoadingRef.current = false;
    }
  }, []);
  
  // Load data on mount
  useEffect(() => {
    loadCurrentUser();
    loadUnreadNotificationsCount();
  }, [loadCurrentUser, loadUnreadNotificationsCount]);

  // Listen for notification refresh events
  useEffect(() => {
    const handleNotificationRefresh = () => {
      // Only refresh if not already loading to prevent redundant calls
      if (!notificationsLoadingRef.current) {
        loadUnreadNotificationsCount();
      }
    };

    window.addEventListener('notificationRefresh', handleNotificationRefresh);
    return () => window.removeEventListener('notificationRefresh', handleNotificationRefresh);
  }, [loadUnreadNotificationsCount]);

  // Listen for server error events
  useEffect(() => {
    const handleServerError = (event) => {
      setServerErrorMessage(event.detail.message);
      setShowServerError(true);
    };

    window.addEventListener('serverError', handleServerError);
    return () => window.removeEventListener('serverError', handleServerError);
  }, []);

  // Auto-show notifications popup when there are unread notifications on initial load
  useEffect(() => {
    const sessionKey = `notifications_auto_shown_${currentUser?.id}`;
    const hasShownThisSession = sessionStorage.getItem(sessionKey) === 'true';
    
    // Only auto-show once per session when user first loads and has notifications
    if (unreadNotificationsCount > 0 && 
        !hasAutoShownNotifications && 
        !showNotifications && 
        !currentUserLoading && 
        currentUser &&
        !hasShownThisSession) {
      setShowNotifications(true);
      setHasAutoShownNotifications(true);
      sessionStorage.setItem(sessionKey, 'true');
    }
  }, [unreadNotificationsCount, hasAutoShownNotifications, showNotifications, currentUserLoading, currentUser]);

  // Function to mark notifications as read
  const markNotificationsAsRead = useCallback(async (notificationIds) => {
    if (notificationIds.length === 0) return;
    
    try {
      await userAPI.markNotificationsRead(notificationIds);
      
      // Update the notification count
      await loadUnreadNotificationsCount();
    } catch (error) {
      console.error('Failed to mark notifications as read:', error);
    }
  }, [loadUnreadNotificationsCount]);
  
  
  const navigate = useNavigate();

  // Tour reference for manual start
  const [tourRef, setTourRef] = useState(null);

  // Force refresh current user data when navigating to settings
  const handleSettingsTabClick = useCallback(() => {
    setActiveTab('settings');
    // Force refresh current user data
  }, []);

  const loadData = useCallback(async () => {
    try {
      setError(''); // Clear any previous errors
      
      if (activeTab === 'users') {
        try {
          const [usersResponse, botsResponse] = await Promise.all([
            userAPI.getUsers(0, 33, 'updated_at', true),
            botAPI.getBots(0, 33, 'updated_at', true)
          ]);
          setUsers(usersResponse.data);
          setBots(botsResponse.data);
        } catch (err) {
          console.error('Failed to load users or bots:', err);
          // Don't set global error for users tab failure
        }
      }
    } catch (err) {
      setError('Failed to load data');
      console.error(err);
    }
  }, [activeTab]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // When opening Settings or when the current user loads, populate bio input
  useEffect(() => {
    if (activeTab === 'settings' && currentUser) {
      setBioText(currentUser.bio || '');
    }
  }, [activeTab, currentUser]);

  useEffect(() => {
    const handleNavigateTab = (e) => {
      const next = e?.detail?.tab;
      if (!next) return;
      if (next === 'users' || next === 'groups' || next === 'stacks') {
        setActiveTab(next);
        setSelectedUser(null);
        setSelectedGroup(null);
        setSelectedStack(null);
      }
    };
    window.addEventListener('navigateTab', handleNavigateTab);
    return () => window.removeEventListener('navigateTab', handleNavigateTab);
  }, []);

  // Allow other components (e.g., Stacks) to request opening a specific group
  
  const handleStackClick = useCallback(async (item) => {
    const itemId = typeof item === 'object' && item !== null ? item.id : item;
    const itemType = typeof item === 'object' && item !== null ? item.type : undefined;
    try {
      // Try normal stack first; if fails, try twisted stack
      try {
        if (itemType === 'twisted') throw new Error('skip-normal');
        const response = await stackAPI.getStack(itemId);
        setSelectedStack(itemId);
        setSelectedTwistedStack(null);
        setSelectedStackName(truncateStackName(response.data));
        setActiveTab('stack');
      } catch (_) {
        try {
          const t = await import('../services/api');
          const response = await t.twistedStackAPI.getTwistedStack(itemId);
          setSelectedTwistedStack(itemId);
          setSelectedStack(null);
          setSelectedStackName((response.data?.name || '').toString().slice(0, 8));
          setActiveTab('stack');
        } catch (e2) {
          throw e2;
        }
      }
    } catch (err) {
      setError('Failed to load stack');
      console.error(err);
    }
  }, []);

  useEffect(() => {
    const handleOpenStack = (e) => {
      const id = e?.detail?.id;
      const type = e?.detail?.type;
      if (!id) return;
      handleStackClick({ id, type });
    };
    window.addEventListener('openStack', handleOpenStack);
    return () => window.removeEventListener('openStack', handleOpenStack);
  }, [handleStackClick]);

  useEffect(() => {
    const stored = localStorage.getItem('theme');
    const initial = (stored === 'light' || stored === 'dark' || stored === 'system') ? stored : 'dark';
    setThemeSetting(initial);
    applyTheme(initial);
  }, []);

  const applyTheme = (mode) => {
    const html = document.documentElement;
    let resolved = mode;
    if (mode === 'system') {
      resolved = window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
    }
    html.setAttribute('data-theme', resolved);
    if (resolved === 'dark') {
      html.classList.add('dark');
    } else {
      html.classList.remove('dark');
    }
  };

  const setThemeAndPersist = (value) => {
    setThemeSetting(value);
    localStorage.setItem('theme', value);
    applyTheme(value);
  };

  const handleLogout = async () => {
    const confirmed = await confirmAction(
      'Log Out',
      'Are you sure you want to log out?',
      {
        confirmText: 'Log Out',
        cancelText: 'Cancel',
        confirmButtonColor: BUTTON_COLORS.LOGOUT,
        cancelButtonColor: BUTTON_COLORS.CANCEL,
      }
    );
    if (!confirmed) return;
    localStorage.removeItem('token');
    window.dispatchEvent(new Event('logout'));
    navigate('/login');
  };

  const renderRelativeLastLogin = (lastLogin) => {
    if (!lastLogin) return '—';
    try {
      const today = new Date();
      const last = new Date(lastLogin + 'T00:00:00');
      const diffMs = today.setHours(0,0,0,0) - last.getTime();
      const days = Math.floor(diffMs / (1000 * 60 * 60 * 24));
      if (days <= 0) return 'today';
      if (days === 1) return '1 day ago';
      return `${days} days ago`;
    } catch (e) {
      return lastLogin;
    }
  };

  const renderSocials = () => {
    if (!selectedUser.socials || selectedUser.socials.length === 0) return null;

    return (
      <div style={{ marginTop: '15px' }}>
        <h4 style={{ marginBottom: '10px', color: 'var(--color-text)' }}>Socials</h4>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          {selectedUser.socials.map((social, index) => {
            const isTelegram = social.platform === 'telegram';
            const icon = isTelegram ? '✈️' : '📱';
            const name = isTelegram ? 'Telegram' : 'Signal';
            
            return (
              <div key={index} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span aria-hidden>{icon}</span>
                <span style={{ fontWeight: 'bold', color: 'var(--color-text)' }}>{name}:</span>
                {social.link ? (
                  <a 
                    href={social.link} 
                    target="_blank" 
                    rel="noreferrer"
                    style={{ 
                      color: 'var(--color-primary)', 
                      textDecoration: 'none',
                      fontWeight: '500'
                    }}
                    onMouseEnter={(e) => e.target.style.textDecoration = 'underline'}
                    onMouseLeave={(e) => e.target.style.textDecoration = 'none'}
                  >
                    {social.value}
                  </a>
                ) : (
                  <span style={{ color: 'var(--color-text)' }}>{social.value}</span>
                )}
                {social.is_verified && (
                  <span style={{ color: 'var(--color-success)', fontSize: '12px' }}>✓</span>
                )}
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  const renderSharedActivity = () => {
    if (!selectedUser) {
      return null;
    }

    if (sharedActivityLoading) {
      return (
        <div style={{ marginTop: '20px' }}>
          <h4 style={{ marginBottom: '10px', color: 'var(--color-text)' }}>Shared Activity</h4>
          <div style={{ color: 'var(--color-text-muted)' }}>Loading...</div>
        </div>
      );
    }

    // Show the section even if no data (will show 0s)
    const friendStacksTotal = sharedActivity ? 
      (sharedActivity.friend_stacks_shared + sharedActivity.friend_twisted_stacks_shared) : 0;
    const groupStacksTotal = sharedActivity ? 
      (sharedActivity.group_stacks_shared + sharedActivity.group_twisted_stacks_shared) : 0;

    return (
      <div style={{ marginTop: '20px' }}>
        <h4 style={{ marginBottom: '10px', color: 'var(--color-text)' }}>Shared Activity</h4>
        <div style={{ 
          border: '1px solid var(--color-border)', 
          borderRadius: '8px', 
          padding: '12px',
          backgroundColor: 'var(--color-surface)'
        }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <tbody>
              <tr>
                <td style={{ 
                  padding: '8px 0', 
                  borderBottom: '1px solid var(--color-border)',
                  fontWeight: '500',
                  color: 'var(--color-text)'
                }}>
                  Friend Stacks
                </td>
                <td style={{ 
                  padding: '8px 0', 
                  borderBottom: '1px solid var(--color-border)',
                  textAlign: 'right',
                  fontWeight: '600',
                  color: 'var(--color-text)'
                }}>
                  {friendStacksTotal}
                </td>
              </tr>
              <tr>
                <td style={{ 
                  padding: '8px 0',
                  fontWeight: '500',
                  color: 'var(--color-text)'
                }}>
                  Group Stacks
                </td>
                <td style={{ 
                  padding: '8px 0',
                  textAlign: 'right',
                  fontWeight: '600',
                  color: 'var(--color-text)'
                }}>
                  {groupStacksTotal}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  const handleUserClick = useCallback(async (userId) => {
    // If clicking on current user, switch to "Settings" tab instead of navigating to profile
    if (currentUser && userId === currentUser.id) {
      setActiveTab('settings');
      setSelectedUser(null);
      setSharedActivity(null);
    } else {
      try {
        setUserProfileLoading(true);
        setError(''); // Clear any previous errors
        
        const response = await userAPI.getUser(userId);
        setSelectedUser(response.data);
        setActiveTab('profile');
        setFriendRequestSent(false);
        
        // Load shared activity for this user
        loadSharedActivity(userId);
      } catch (err) {
        setError('Failed to load user profile');
        console.error(err);
      } finally {
        setUserProfileLoading(false);
      }
    }
  }, [currentUser]);

  const handleBotClick = useCallback(async (botId) => {
    try {
      const response = await botAPI.getBot(botId);
      setSelectedBot(response.data);
      setActiveTab('bot');
    } catch (err) {
      console.error('Failed to load bot:', err);
      setError('Failed to load bot profile');
    }
  }, []);

  const handleAvatarChange = async (avatarId) => {
    try {
      await userAPI.updateAvatar(avatarId);
      setAvatarMessage('Updated avatar');
      
      // Refresh current user data
      await loadCurrentUser();
      
      // Clear message after 3 seconds
      setTimeout(() => setAvatarMessage(''), 3000);
    } catch (err) {
      setError('Failed to update avatar');
      console.error(err);
    }
  };

  const handleBioSave = async (text) => {
    try {
      setUpdateBioLoading(true);
      await userAPI.updateBio(text);
      setBioMessage('Bio updated successfully');
      
      // Refresh current user data
      await loadCurrentUser();
      
      // Clear message after 3 seconds
      setTimeout(() => setBioMessage(''), 3000);
    } catch (err) {
      setError('Failed to update bio');
      console.error(err);
      throw err; // Re-throw to let EditableTextArea handle the error
    } finally {
      setUpdateBioLoading(false);
    }
  };

  const handleBioRevert = () => {
    setBioText(currentUser?.bio || '');
  };

  const handleBackToUsers = () => {
    setActiveTab('users');
    setSelectedUser(null);
    setSharedActivity(null);
    setUserProfileLoading(false);
  };

  const loadSharedActivity = async (userId) => {
    try {
      setSharedActivityLoading(true);
      const response = await userAPI.getUserSharedActivity(userId);
      setSharedActivity(response.data);
    } catch (err) {
      console.error('Failed to load shared activity:', err);
      // Don't set error state for shared activity failures
      setSharedActivity(null);
    } finally {
      setSharedActivityLoading(false);
    }
  };

  const handleGroupClick = useCallback(async (groupId) => {
    try {
      const response = await groupAPI.getGroup(groupId);
      setSelectedGroup(groupId);
      setSelectedGroupName(response.data.name);
      setActiveTab('group');
      setShowGroups(false);
    } catch (err) {
      setError('Failed to load group profile');
      console.error(err);
    }
  }, []);

  useEffect(() => {
    const handleOpenGroup = (e) => {
      const groupId = e?.detail?.groupId;
      if (!groupId) return;
      handleGroupClick(groupId);
    };
    window.addEventListener('openGroup', handleOpenGroup);
    return () => window.removeEventListener('openGroup', handleOpenGroup);
  }, [handleGroupClick]);

  // Allow other components to request opening a specific user's profile tab
  useEffect(() => {
    const handleOpenUser = (e) => {
      const userId = e?.detail?.userId;
      if (!userId) return;
      handleUserClick(userId);
    };
    window.addEventListener('openUser', handleOpenUser);
    return () => window.removeEventListener('openUser', handleOpenUser);
  }, [handleUserClick]);

  // Allow other components to request opening a specific bot's profile
  useEffect(() => {
    const handleOpenBot = (e) => {
      const botId = e?.detail?.botId;
      if (!botId) return;
      handleBotClick(botId);
    };
    window.addEventListener('openBot', handleOpenBot);
    return () => window.removeEventListener('openBot', handleOpenBot);
  }, [handleBotClick]);

  const handleBackToGroups = () => {
    setActiveTab('groups');
    setSelectedGroup(null);
    setSelectedGroupName(null);
  };

  const handleBackToStacks = () => {
    setActiveTab('stacks');
    setSelectedBot(null);
  };

  const truncateStackName = (stack) => getStackDisplayName(stack, 8);

  const handleCreateGroup = async () => {
    try {
      const response = await groupAPI.createGroup(createGroupForm);
      setShowCreateGroupDialog(false);
      setCreateGroupForm({ name: '', description: '', is_public: true, is_restricted: false, is_readonly: false });
      // Navigate to the new group
      await handleGroupClick(response.data.id);
    } catch (err) {
      setError('Failed to create group');
      console.error(err);
    }
  };

  const handleSendFriendRequest = async () => {
    if (!selectedUser) return;
    
    try {
      await userAPI.sendFriendRequest(selectedUser.id);
      setFriendRequestSent(true);
      // Reload user data to get updated friend status
      const response = await userAPI.getUser(selectedUser.id);
      setSelectedUser(response.data);
      
      // Clear notification cache and trigger refresh
      apiCache.delete('notifications');
      apiCache.delete('unreadNotificationsCount');
      window.dispatchEvent(new Event('notificationRefresh'));
    } catch (err) {
      setError('Failed to send friend request');
      console.error(err);
    }
  };

  const handleUnfriendUser = async () => {
    if (!selectedUser) return;
    
    try {
      await userAPI.unfriendUser(selectedUser.id);
      setShowUnfriendConfirm(false);
      // Reload user data to get updated friend status
      const response = await userAPI.getUser(selectedUser.id);
      setSelectedUser(response.data);
    } catch (err) {
      setError('Failed to unfriend user');
      console.error(err);
    }
  };

  const handleBlockUser = async () => {
    if (!selectedUser) return;
    
    try {
      await userAPI.blockUser(selectedUser.id);
      setShowBlockConfirm(false);
      // Redirect back to users page
      handleBackToUsers();
    } catch (err) {
      setError('Failed to block user');
      console.error(err);
    }
  };

  const handleAcceptFriendRequest = async () => {
    if (!selectedUser) return;
    
    try {
      // Get the friend request ID from the user data
      const response = await userAPI.getFriendRequests('incoming');
      const incomingRequest = response.data.find(req => req.from_user_id === selectedUser.id);
      
      if (incomingRequest) {
        await userAPI.updateFriendRequest(incomingRequest.id, 'ACCEPTED');
        // Mark the notification as read
        const notificationsResponse = await userAPI.getNotifications();
        const notification = notificationsResponse.data.find(n => 
          n.type === 'FRIEND_REQUEST' && 
          n.data.from_user_id === selectedUser.id
        );
        if (notification) {
          await userAPI.markNotificationsRead([notification.id]);
        }
        // Refresh the page to update the UI
        window.location.reload();
      }
    } catch (err) {
      setError('Failed to accept friend request');
      console.error(err);
    }
  };

  const handleIgnoreFriendRequest = async () => {
    if (!selectedUser) return;
    
    try {
      // Get the friend request ID from the user data
      const response = await userAPI.getFriendRequests('incoming');
      const incomingRequest = response.data.find(req => req.from_user_id === selectedUser.id);
      
      if (incomingRequest) {
        await userAPI.updateFriendRequest(incomingRequest.id, 'REJECTED');
        // Mark the notification as read
        const notificationsResponse = await userAPI.getNotifications();
        const notification = notificationsResponse.data.find(n => 
          n.type === 'FRIEND_REQUEST' && 
          n.data.from_user_id === selectedUser.id
        );
        if (notification) {
          await userAPI.markNotificationsRead([notification.id]);
        }
        // Refresh the page to update the UI
        window.location.reload();
      }
    } catch (err) {
      setError('Failed to ignore friend request');
      console.error(err);
    }
  };

  const handleDeactivateAccount = async () => {
    try {
      await userAPI.deactivateAccount(true);
      setShowDeactivateConfirm(false);
      // Log out the user after deactivation
      handleLogout();
    } catch (err) {
      setError('Failed to deactivate account');
      console.error(err);
    }
  };


  const handleCreateStackSuccess = useCallback(async (stackData) => {
    // Refresh the stacks view or navigate to the new stack
    try {
      console.log('Stack created successfully:', stackData);
      
      // If it's a draft stack, just stay on dashboard (don't try to open it)
      if (stackData?.is_draft) {
        console.log('Draft stack created, staying on dashboard');
        return;
      }
      
      // For published stacks, you could refresh the stacks list here if needed
    } catch (_) {}
  }, []);






  const getFriendStatusText = (status) => {
    switch (status) {
      case 'friends':
        return 'Status: friends';
      case 'pending':
        return 'Status: pending';
      case 'incoming':
        return 'Status: incoming request';
      default:
        return 'Status: not friends';
    }
  };

  const getFriendStatusColor = (status) => {
    switch (status) {
      case 'friends':
        return 'var(--color-success)';
      case 'pending':
        return 'var(--color-warning)';
      case 'incoming':
        return 'var(--color-info)';
      default:
        return 'var(--color-text-muted)';
    }
  };

  if (currentUserLoading) {
    return <div className="container">Loading...</div>;
  }

  if (currentUserError) {
    return (
      <div className="container">
        <div style={{ color: 'red', marginBottom: '20px' }}>
          {currentUserError?.response?.status === 401 
            ? 'Authentication failed. Please log in again.' 
            : 'Failed to load user data'}
        </div>
        <button onClick={() => window.location.reload()} className="btn">Retry</button>
      </div>
    );
  }

  return (
    <div data-tour="app-root">
      <header className="header">
        <div className="container">
            <div className="header-content">
            <h1 style={{ cursor: 'pointer' }} onClick={createSortaFriendsClickHandler(navigate, {
              setActiveTab,
              setSelectedUser,
              setSelectedGroup,
              setSelectedStack
            })}>SortaFriends</h1>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              {currentUser && (
                <>
                  <div 
                    className="notification-icon"
                    onClick={() => setShowNotifications(true)}
                    title="Notifications"
                    data-tour="notifications-envelope"
                  >
                    <svg 
                      xmlns="http://www.w3.org/2000/svg" 
                      viewBox="0 0 24 24" 
                      width="20" height="20" aria-hidden="true"
                    >
                      <path d="M20 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z"/>
                    </svg>
                    {unreadNotificationsCount > 0 && (
                      <div className="notification-badge">
                        {unreadNotificationsCount}
                      </div>
                    )}
                  </div>
                  <button
                    type="button"
                    className="notification-icon"
                    title="Settings"
                    aria-label="Settings"
                    data-tour="settings-button"
                    onClick={() => {
                      handleSettingsTabClick();
                      setSelectedUser(null);
                    }}
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="20" height="20" aria-hidden="true" fill="currentColor">
                      <path d="M19.14,12.94a7.49,7.49,0,0,0,.05-.94,7.49,7.49,0,0,0-.05-.94l2.11-1.65a.5.5,0,0,0,.12-.64l-2-3.46a.5.5,0,0,0-.6-.22l-2.49,1a7.14,7.14,0,0,0-1.63-.94l-.38-2.65A.5.5,0,0,0,13.63,2H10.37a.5.5,0,0,0-.5.42L9.49,5.07a7.14,7.14,0,0,0-1.63.94l-2.49-1a.5.5,0,0,0-.6.22l-2,3.46a.5.5,0,0,0,.12.64L5.14,11.06a7.49,7.49,0,0,0-.05.94,7.49,7.49,0,0,0,.05.94L3,14.59a.5.5,0,0,0-.12.64l2,3.46a.5.5,0,0,0,.6.22l2.49-1a7.14,7.14,0,0,0,1.63.94l.38,2.65a.5.5,0,0,0,.5.42h3.26a.5.5,0,0,0,.5-.42l.38-2.65a7.14,7.14,0,0,0,1.63-.94l2.49,1a.5.5,0,0,0,.6-.22l2-3.46a.5.5,0,0,0-.12-.64ZM12,15.5A3.5,3.5,0,1,1,15.5,12,3.5,3.5,0,0,1,12,15.5Z"/>
                    </svg>
                  </button>
                </>
              )}
              <button
                onClick={handleLogout}
                className="notification-icon"
                title="Logout"
                aria-label="Logout"
              >
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="20" height="20" aria-hidden="true" fill="currentColor">
                  <path d="M10 17l1.41-1.41L8.83 13H20v-2H8.83l2.58-2.59L10 7l-5 5 5 5z"/>
                  <path d="M4 19h2v2H4c-1.1 0-2-.9-2-2V5c0-1.1.9-2 2-2h2v2H4v14z"/>
                </svg>
              </button>
            </div>
          </div>
        </div>
      </header>

      <div className="container">
        <Tour
          currentUser={currentUser}
          currentUserLoading={currentUserLoading}
          activeTab={activeTab}
          setActiveTab={setActiveTab}
          onTourRef={setTourRef}
        />

        <div className="nav-tabs">
          <button
            className={`nav-tab ${activeTab === 'stacks' ? 'active' : ''}`}
            data-tour="stacks-tab"
            onClick={() => {
              setActiveTab('stacks');
              setSelectedUser(null);
              setSelectedGroup(null);
              setSelectedStack(null);
            }}
          >
            Stacks
          </button>
          <button
            className={`nav-tab ${activeTab === 'groups' ? 'active' : ''}`}
            data-tour="groups-tab"
            onClick={() => {
              setActiveTab('groups');
              setSelectedUser(null);
              setSelectedGroup(null);
            }}
          >
            Groups
          </button>
          <button
            className={`nav-tab ${activeTab === 'users' ? 'active' : ''}`}
            data-tour="users-tab"
            onClick={() => {
              setActiveTab('users');
              setSelectedUser(null);
            }}
          >
            Users
          </button>

          {selectedUser && (
            <button
              className={`nav-tab ${activeTab === 'profile' ? 'active' : ''}`}
              onClick={() => setActiveTab('profile')}
            >
              {selectedUser.username}
            </button>
          )}

          {selectedBot && (
            <button
              className={`nav-tab ${activeTab === 'bot' ? 'active' : ''}`}
              onClick={() => setActiveTab('bot')}
            >
              {selectedBot.name}
            </button>
          )}
          {selectedGroup && (
            <button
              className={`nav-tab ${activeTab === 'group' ? 'active' : ''}`}
              onClick={() => setActiveTab('group')}
            >
              {selectedGroupName || 'Group'}
            </button>
          )}
          {selectedStack && (
            <button
              className={`nav-tab ${activeTab === 'stack' ? 'active' : ''}`}
              onClick={() => setActiveTab('stack')}
            >
              {selectedStackName || 'Stack'}
            </button>
          )}
        </div>

        {error && (
          <div style={{ 
            color: 'var(--color-danger)', 
            marginBottom: '20px',
            padding: '12px',
            backgroundColor: 'var(--color-surface)',
            border: '1px solid var(--color-danger)',
            borderRadius: '6px',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: '12px'
          }}>
            <span>{error}</span>
            <button
              onClick={() => {
                setError('');
                loadData();
              }}
              className="btn"
              style={{ 
                backgroundColor: 'var(--color-primary)', 
                color: '#fff', 
                border: 'none',
                fontSize: '14px',
                padding: '6px 12px'
              }}
            >
              Retry
            </button>
          </div>
        )}
        {avatarMessage && <div style={{ color: 'green', marginBottom: '20px' }}>{avatarMessage}</div>}
        {bioMessage && <div style={{ color: 'green', marginBottom: '20px' }}>{bioMessage}</div>}

        {activeTab === 'users' && (
          <div>
            {/* Bots Section */}
            {bots.length > 0 && (
              <div style={{ marginBottom: '30px' }}>
                <h2 style={{ marginBottom: '20px' }}>Bots</h2>
                <div className="grid">
                  {bots.map((bot) => (
                    <div 
                      key={bot.id} 
                      className="user-card"
                      style={{ cursor: 'pointer' }}
                      onClick={() => handleBotClick(bot.id)}
                    >
                      <img 
                        src={`/assets/${bot.avatar_id}`} 
                        alt={`${bot.name} avatar`}
                        className="user-avatar"
                      />
                      <div className="user-info">
                        <div className="user-name">
                          {bot.name}
                        </div>
                        <div style={{ color: 'var(--color-text-muted)', fontSize: '12px', textTransform: 'lowercase' }}>
                          {bot.type.toLowerCase().replace('_', ' ')}
                        </div>
                        <div style={{ color: 'var(--color-text-muted)', fontSize: '14px' }}>
                          {bot.bio ? `"${bot.bio}"` : 'Bot'}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Users Section */}
            <div>
              <h2 style={{ marginBottom: '20px' }}>Users</h2>
              <div className="grid">
                {(function() {
                  const isSelf = (u) => currentUser && u.id === currentUser.id;
                  // Build list with current user first (if available), then others
                  const combined = [];
                  if (currentUser) {
                    combined.push({ ...currentUser, isSelf: true });
                  }
                  combined.push(
                    ...users
                      .filter(u => !isSelf(u))
                      .map(u => ({ ...u, isSelf: false }))
                  );

                  combined.sort((a, b) => {
                    // Always put current user first
                    if (a.isSelf && !b.isSelf) return -1;
                    if (b.isSelf && !a.isSelf) return 1;
                    
                    // For non-self users, maintain server-side updated_at ordering
                    // The server already returns users sorted by updated_at DESC
                    // so we just need to preserve that order
                    return 0;
                  });

                  return combined.map(user => (
                  <div 
                    key={user.id} 
                    className="user-card"
                    style={{ cursor: 'pointer' }}
                    onClick={() => handleUserClick(user.id)}
                  >
                    <img 
                      src={getDiceBearUserAvatar(user.username, user.avatar_id)} 
                      alt={`${user.username} avatar`}
                      className="user-avatar"
                    />
                    <div className="user-info">
                      <div className="user-name">
                        {user.username}{user.isSelf ? ' (me)' : ''}
                      </div>
                      <div style={{ color: 'var(--color-text-muted)', fontSize: '14px' }}>
                        {user.bio ? `"${user.bio}"` : 'No bio yet'}
                      </div>
                      {user.friend_status && (
                        <div style={{ 
                          color: getFriendStatusColor(user.friend_status), 
                          fontSize: '12px', 
                          fontWeight: 'bold',
                          marginTop: '5px'
                        }}>
                          {getFriendStatusText(user.friend_status)}
                        </div>
                      )}
                    </div>
                  </div>
                  ));
                })()}
              </div>
            </div>
          </div>
        )}

        {activeTab === 'groups' && (
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h2>Groups</h2>
              <button 
                onClick={() => setShowCreateGroupDialog(true)}
                className="btn"
                style={{ backgroundColor: 'var(--color-success)', color: 'var(--text-on-success)' }}
              >
                Add Group
              </button>
            </div>
            
            <Groups 
              onClose={() => setShowGroups(false)}
              onGroupClick={handleGroupClick}
              isEmbedded={true}
            />
          </div>
        )}

        {activeTab === 'stacks' && (
          <Stacks onClose={() => setActiveTab('users')} onStackClick={handleStackClick} isEmbedded={true} currentUser={currentUser} />
        )}

        {activeTab === 'group' && selectedGroup && (
          <GroupProfile 
            groupId={selectedGroup}
            onBack={handleBackToGroups}
            onClose={() => setActiveTab('groups')}
          />
        )}

        {activeTab === 'stack' && selectedStack && (
          <StackProfile
            stackId={selectedStack}
            onBack={handleBackToStacks}
            onClose={() => setActiveTab('stacks')}
          />
        )}

        {activeTab === 'stack' && selectedTwistedStack && (
          <TwistedStackProfile
            twistedId={selectedTwistedStack}
            onBack={handleBackToStacks}
            onClose={() => setActiveTab('stacks')}
          />
        )}

        {activeTab === 'profile' && (
          <div className="card">
            {userProfileLoading ? (
              <div style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
                <div style={{ 
                  width: '100px', 
                  height: '100px', 
                  borderRadius: '50%',
                  backgroundColor: 'var(--color-border)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <div style={{ color: 'var(--color-text-muted)' }}>Loading...</div>
                </div>
                <div>
                  <h2>Loading user profile...</h2>
                  <p style={{ color: 'var(--color-text-muted)' }}>Please wait while we fetch the user details.</p>
                </div>
              </div>
            ) : selectedUser ? (
              <div style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
                <img 
                  src={getDiceBearUserAvatar(selectedUser.username, selectedUser.avatar_id)} 
                  alt={`${selectedUser.username} avatar`}
                  style={{ width: '100px', height: '100px', borderRadius: '50%' }}
                />
                <div>
                  <h2>{selectedUser.username}</h2>
                  {selectedUser.bio && (
                    <p style={{ fontStyle: 'italic', color: 'var(--color-text)', opacity: 0.9, marginBottom: '10px' }}>
                      "{selectedUser.bio}"
                    </p>
                  )}
                  {/* ignore roles while beta testing */}
                  {/* <p>
                    <strong>Role:</strong> <strong>{formatRoleDisplay(selectedUser.role)}</strong>
                  </p> */}
                  {selectedUser.friend_status && (
                    <p style={{ 
                      color: getFriendStatusColor(selectedUser.friend_status),
                      fontWeight: 'bold'
                    }}>
                      <strong>{getFriendStatusText(selectedUser.friend_status)}</strong>
                    </p>
                  )}
                  <p>
                    <strong>Last Login:</strong> <strong>{renderRelativeLastLogin(selectedUser.last_login)}</strong>
                  </p>
                  {renderSocials()}
                  {renderSharedActivity()}
                </div>
              </div>
            ) : (
              <div>
                <h2>User not found</h2>
                <p style={{ color: 'var(--color-text-muted)' }}>The requested user could not be found.</p>
              </div>
            )}
            {!userProfileLoading && selectedUser && (
              <div style={{ marginTop: '20px', display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                <button onClick={handleBackToUsers} className="btn" style={{ backgroundColor: 'var(--color-primary)', color: 'var(--text-on-primary)' }}>
                  Back
                </button>
                
                {selectedUser.friend_status === 'friends' && (
                <>
                  <button 
                    onClick={() => {
                      setCreateStackDialog({
                        isOpen: true,
                        mode: 'friend',
                        targetId: selectedUser.id,
                        targetName: selectedUser.username,
                        targetAvatar: getDiceBearUserAvatar(selectedUser.username, selectedUser.avatar_id)
                      });
                    }}
                    className="btn"
                    style={{ backgroundColor: 'var(--color-success)', color: 'var(--text-on-success)' }}
                  >
                    Create Stack
                  </button>
                  <button 
                    onClick={() => setShowUnfriendConfirm(true)}
                    className="btn"
                    style={{ backgroundColor: 'var(--color-warning)', color: 'var(--text-on-warning)' }}
                  >
                    Unfriend
                  </button>
                </>
              )}
              
              {selectedUser.friend_status === 'not_friends' && (
                <button 
                  onClick={handleSendFriendRequest}
                  className="btn"
                  style={{ backgroundColor: 'var(--color-success)', color: 'var(--text-on-success)' }}
                  disabled={friendRequestSent}
                  data-tour="send-friend-request"
                >
                  {friendRequestSent ? 'Friend request sent' : 'Friend User'}
                </button>
              )}
              
              {selectedUser.friend_status === 'incoming' && (
                <>
                  <button 
                    onClick={handleAcceptFriendRequest}
                    className="btn"
                    style={{ backgroundColor: 'var(--color-success)', color: 'var(--text-on-success)' }}
                  >
                    Accept Friend Request
                  </button>
                  <button 
                    onClick={handleIgnoreFriendRequest}
                    className="btn"
                    style={{ backgroundColor: 'var(--color-border)', color: 'var(--color-text)' }}
                  >
                    Ignore Friend Request
                  </button>
                </>
              )}
              
                <button 
                  onClick={() => setShowBlockConfirm(true)}
                  className="btn"
                  style={{ backgroundColor: 'var(--color-danger)', color: 'var(--text-on-danger)' }}
                >
                  Block
                </button>
              </div>
            )}
          </div>
        )}

        {activeTab === 'bot' && selectedBot && (
          <BotProfile 
            botId={selectedBot.id}
            onBack={handleBackToStacks}
            onClose={() => setActiveTab('stacks')}
          />
        )}

        {/* Confirmation Modals */}
        {showUnfriendConfirm && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'var(--overlay)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000
          }}>
            <div style={{
              backgroundColor: 'var(--color-surface)',
              padding: '20px',
              borderRadius: '8px',
              maxWidth: '400px',
              textAlign: 'center'
            }}>
              <h3>Are you sure?</h3>
              <p>This will remove {selectedUser?.username} from your friends list.</p>
              <div style={{ display: 'flex', gap: '10px', justifyContent: 'center', marginTop: '20px' }}>
                <button 
                  onClick={handleUnfriendUser}
                  className="btn"
                  style={{ backgroundColor: 'var(--color-warning)', color: 'var(--text-on-warning)' }}
                >
                  Yes, Unfriend
                </button>
                <button 
                  onClick={() => setShowUnfriendConfirm(false)}
                  className="btn"
                  style={{ backgroundColor: 'var(--color-border)', color: 'var(--color-text)' }}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}

        {showBlockConfirm && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'var(--overlay)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000
          }}>
            <div style={{
              backgroundColor: 'var(--color-surface)',
              padding: '20px',
              borderRadius: '8px',
              maxWidth: '400px',
              textAlign: 'center'
            }}>
              <h3>Are you sure?</h3>
              <p>This will block {selectedUser?.username}. This action cannot be undone.</p>
              <div style={{ display: 'flex', gap: '10px', justifyContent: 'center', marginTop: '20px' }}>
                <button 
                  onClick={handleBlockUser}
                  className="btn"
                  style={{ backgroundColor: 'var(--color-danger)', color: 'var(--text-on-danger)' }}
                >
                  Yes, Block
                </button>
                <button 
                  onClick={() => setShowBlockConfirm(false)}
                  className="btn"
                  style={{ backgroundColor: 'var(--color-border)', color: 'var(--color-text)' }}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}

        {showDeactivateConfirm && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'var(--overlay)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000
          }}>
            <div style={{
              backgroundColor: 'var(--color-surface)',
              padding: '20px',
              borderRadius: '8px',
              maxWidth: '400px',
              textAlign: 'center'
            }}>
              <h3 style={{ color: 'var(--color-danger)' }}>Deactivate Account</h3>
              <p>Are you sure you want to deactivate your account?</p>
              <p style={{ fontSize: '14px', color: 'var(--color-text-muted)', marginTop: '10px' }}>
                This action cannot be undone. You will be logged out immediately and will not be able to log back in.
              </p>
              <div style={{ display: 'flex', gap: '10px', justifyContent: 'center', marginTop: '20px' }}>
                <button 
                  onClick={handleDeactivateAccount}
                  className="btn"
                  style={{ backgroundColor: 'var(--color-danger)', color: 'white' }}
                >
                  Yes, Deactivate
                </button>
                <button 
                  onClick={() => setShowDeactivateConfirm(false)}
                  className="btn"
                  style={{ backgroundColor: 'var(--color-border)', color: 'var(--color-text)' }}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'settings' && currentUserLoading && (
          <div className="card">
            <div style={{ textAlign: 'center', padding: '40px' }}>
              <p>Loading settings...</p>
            </div>
          </div>
        )}
        
        {activeTab === 'settings' && currentUser && !currentUserLoading && (
          <div className="card">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '30px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
                <img 
                  src={getDiceBearUserAvatar(currentUser.username, currentUser.avatar_id)} 
                  alt={`${currentUser.username} avatar`}
                  style={{ width: '100px', height: '100px', borderRadius: '50%' }}
                />
                <div>
                  <h2>Settings</h2>
                  <p><strong>Username:</strong> <strong>{currentUser.username}</strong></p>
                  <p><strong>Email:</strong> <strong>{currentUser.email}</strong></p>
                  {/* ignore roles while beta testing */}
                  {/* <p><strong>Role:</strong> <strong>{formatRoleDisplay(currentUser.role)}</strong></p> */}
                </div>
              </div>
              <button 
                onClick={() => setActiveTab('users')}
                className="close-btn"
                style={{ fontSize: '24px', padding: '0', width: '30px', height: '30px' }}
                title="Close Settings"
              >
                &times;
              </button>
            </div>
            
            <EditableTextArea
              value={bioText}
              onSave={handleBioSave}
              onRevert={handleBioRevert}
              placeholder="Tell us about yourself..."
              maxLength={120}
              label="Bio"
              loading={updateBioLoading}
            />

            <div style={{ marginTop: '30px' }}>
              <h3>Theme</h3>
              <div style={{ display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap' }}>
                {['system', 'light', 'dark'].map((option) => (
                  <button
                    key={option}
                    className="btn"
                    onClick={() => setThemeAndPersist(option)}
                    style={{
                      backgroundColor: themeSetting === option ? 'var(--color-primary)' : 'var(--color-border)',
                      color: themeSetting === option ? 'var(--text-on-primary)' : 'var(--color-text)',
                      border: 'none',
                    }}
                    aria-pressed={themeSetting === option}
                  >
                    {option.charAt(0).toUpperCase() + option.slice(1)}
                  </button>
                ))}
              </div>
            </div>
            
            
              <div style={{ marginTop: '30px' }}>
              <h3>Choose Avatar</h3>
              <div data-tour="avatar-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '20px', marginTop: '15px' }}>
                {[1, 2, 3, 4, 5, 6, 7, 8, 9].map(avatarId => (
                  <div key={avatarId} style={{ textAlign: 'center' }}>
                    <label style={{ cursor: 'pointer', display: 'block' }}>
                      <input
                        type="radio"
                        name="avatar"
                        value={avatarId}
                        checked={currentUser.avatar_id === avatarId}
                        onChange={() => handleAvatarChange(avatarId)}
                        style={{ marginRight: '8px' }}
                      />
                      <img 
                        src={getDiceBearUserAvatar(currentUser.username, avatarId)} 
                        alt={`Avatar ${avatarId}`}
                        style={{ 
                          width: '60px', 
                          height: '60px', 
                          borderRadius: '50%',
                            border: currentUser.avatar_id === avatarId ? '3px solid var(--color-primary)' : '1px solid var(--color-border)'
                        }}
                      />
                    </label>
                  </div>
                ))}
              </div>
            </div>

            <div style={{ marginTop: '30px' }}>
              <SocialSettings socials={currentUser?.socials || []} />
            </div>

            <div style={{ marginTop: '30px' }}>
              <h3>Help</h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                <button
                  type="button"
                  className="btn"
                  style={{ backgroundColor: 'var(--color-primary)', color: 'var(--text-on-primary)', border: 'none' }}
                  onClick={() => {
                    if (tourRef) {
                      tourRef.startTour();
                    }
                  }}
                >
                  Show tour again
                </button>
                <a
                  href="/terms-of-service"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn"
                  style={{ 
                    backgroundColor: 'var(--color-border)', 
                    color: 'var(--color-text)', 
                    border: 'none',
                    textDecoration: 'none',
                    textAlign: 'center'
                  }}
                >
                  Terms of Service
                </a>
                <a
                  href="/rules"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn"
                  style={{ 
                    backgroundColor: 'var(--color-border)', 
                    color: 'var(--color-text)', 
                    border: 'none',
                    textDecoration: 'none',
                    textAlign: 'center'
                  }}
                >
                  Community Rules
                </a>
              </div>
            </div>

            <div style={{ marginTop: '30px', borderTop: '1px solid var(--color-border)', paddingTop: '30px' }}>
              <h3 style={{ color: 'var(--color-danger)' }}>Danger Zone</h3>
              <p style={{ color: 'var(--color-text-muted)', marginBottom: '20px' }}>
                Once you deactivate your account, you will not be able to log in or interact with other users.
              </p>
              <button 
                onClick={() => setShowDeactivateConfirm(true)}
                className="btn"
                style={{ 
                    backgroundColor: 'var(--color-danger)', 
                  color: 'white',
                  border: 'none'
                }}
              >
                Deactivate Account
              </button>
            </div>
          </div>
        )}
      </div>
      
      {showNotifications && (
        <Notifications 
          notifications={notificationsData}
          onClose={async () => {
            setShowNotifications(false);
            setHasAutoShownNotifications(false);
            
            // Mark current notifications as read when popup is dismissed
            if (currentNotificationIds.length > 0) {
              await markNotificationsAsRead(currentNotificationIds);
            }
            
            // Dispatch event to notify other components of notification changes
            // Note: markNotificationsAsRead already calls loadUnreadNotificationsCount
            window.dispatchEvent(new Event('notificationRefresh'));
          }} 
        />
      )}
      
      {showGroups && (
        <Groups 
          onClose={() => setShowGroups(false)}
          onGroupClick={handleGroupClick}
        />
      )}

      {/* Create Group Dialog */}
      {showCreateGroupDialog && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'var(--overlay)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1001
        }}>
          <div style={{
            backgroundColor: 'var(--color-surface)',
            color: 'var(--color-text)',
            padding: '20px',
            borderRadius: '8px',
            maxWidth: '400px',
            width: '100%',
            boxShadow: '0 4px 6px var(--color-shadow)'
          }}>
            <h3>Create New Group</h3>
            <div style={{ marginBottom: '15px' }}>
              <label style={{ display: 'block', marginBottom: '5px' }}>
                Group Name (max 24 characters):
              </label>
              <input
                type="text"
                value={createGroupForm.name}
                onChange={(e) => setCreateGroupForm({...createGroupForm, name: e.target.value})}
                maxLength={24}
                style={{
                  width: '100%',
                  padding: '8px',
                  border: '1px solid var(--color-border)',
                  borderRadius: '4px',
                  background: 'var(--color-surface)',
                  color: 'var(--color-text)'
                }}
              />
            </div>
            <div style={{ marginBottom: '15px' }}>
              <label style={{ display: 'block', marginBottom: '5px' }}>
                Description (optional, max 64 characters):
              </label>
              <input
                type="text"
                value={createGroupForm.description}
                onChange={(e) => setCreateGroupForm({
                  ...createGroupForm,
                  description: e.target.value
                })}
                maxLength={64}
                style={{
                  width: '100%',
                  padding: '8px',
                  border: '1px solid var(--color-border)',
                  borderRadius: '4px',
                  background: 'var(--color-surface)',
                  color: 'var(--color-text)'
                }}
              />
            </div>
            <div style={{ marginBottom: '15px' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <input
                  type="checkbox"
                  checked={createGroupForm.is_public}
                  onChange={(e) => setCreateGroupForm({...createGroupForm, is_public: e.target.checked})}
                />
                Public Group
              </label>
            </div>
            <div style={{ marginBottom: '15px' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <input
                  type="checkbox"
                  checked={createGroupForm.is_restricted}
                  onChange={(e) => setCreateGroupForm({...createGroupForm, is_restricted: e.target.checked})}
                />
                Restricted Group
              </label>
              <div style={{ 
                fontSize: '12px', 
                fontStyle: 'italic', 
                color: 'var(--color-text-muted)', 
                marginLeft: '24px',
                marginTop: '4px'
              }}>
                Only the owner is allowed to invite users for restricted groups.
              </div>
            </div>
            <div style={{ marginBottom: '15px' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <input
                  type="checkbox"
                  checked={createGroupForm.is_readonly}
                  onChange={(e) => setCreateGroupForm({...createGroupForm, is_readonly: e.target.checked})}
                />
                Read-only Group
              </label>
              <div style={{ 
                fontSize: '12px', 
                fontStyle: 'italic', 
                color: 'var(--color-text-muted)', 
                marginLeft: '24px',
                marginTop: '4px'
              }}>
               Only the owner can create new stacks for read-only groups.
              </div>
            </div>
            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
              <button 
                onClick={() => setShowCreateGroupDialog(false)}
                className="btn"
                style={{ backgroundColor: 'var(--color-border)', color: 'var(--color-text)' }}
              >
                Cancel
              </button>
              <button 
                onClick={handleCreateGroup}
                className="btn"
                style={{ backgroundColor: 'var(--color-success)', color: 'var(--text-on-success)' }}
                disabled={!createGroupForm.name.trim()}
              >
                Add Group
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Create User Stack Dialog */}
      <CreateStack
        isOpen={createStackDialog.isOpen}
        onClose={() => setCreateStackDialog({ isOpen: false, mode: null, targetId: null, targetName: null, targetAvatar: null })}
        onSuccess={handleCreateStackSuccess}
        mode={createStackDialog.mode}
        targetId={createStackDialog.targetId}
        targetName={createStackDialog.targetName}
        targetAvatar={createStackDialog.targetAvatar}
      />

      {/* Server Error Overlay */}
      {showServerError && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'var(--overlay)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 10000
        }}>
          <div style={{
            backgroundColor: 'var(--color-surface)',
            padding: '40px',
            borderRadius: '12px',
            maxWidth: '500px',
            width: '90%',
            textAlign: 'center',
            boxShadow: '0 8px 32px var(--color-shadow)'
          }}>
            <img
              src="/assets/temporary-issue.png"
              alt="Temporary issue illustration"
              style={{ maxWidth: '320px', width: '100%', marginBottom: '24px' }}
            />
            <h1 style={{ fontSize: '28px', margin: '0 0 12px', color: 'var(--color-text)' }}>
              {serverErrorMessage}
            </h1>
            <p style={{ marginBottom: '24px', color: 'var(--color-text-muted)' }}>
              Please try again in a moment.
            </p>
            <div style={{ display: 'flex', justifyContent: 'center', gap: '12px', flexWrap: 'wrap' }}>
              <button
                onClick={() => {
                  setShowServerError(false);
                  window.location.href = '/dashboard';
                }}
                className="btn"
                style={{ 
                  backgroundColor: 'var(--color-primary)', 
                  color: '#fff', 
                  border: 'none',
                  padding: '12px 24px',
                  fontSize: '16px'
                }}
              >
                Go to Dashboard
              </button>
              <button
                onClick={() => window.location.reload()}
                className="btn"
                style={{ 
                  backgroundColor: 'var(--color-border)', 
                  color: 'var(--color-text)', 
                  border: 'none',
                  padding: '12px 24px',
                  fontSize: '16px'
                }}
              >
                Reload Page
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Dashboard; 